/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useContext } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Cell } from "react-table";
import { Box, Text } from "@nordcloud/gnui";
import { FlexContainer, Table } from "~/components";
import { ABConfigurationAdd } from "~/components/Applications/ApplicationDetails/ABConfigurationAdd";
import { ABConfigurationRemove } from "~/components/Applications/ApplicationDetails/ABConfigurationRemove";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { DetailRow } from "~/components/Utils/DetailRow";
import { ABConfiguration } from "~/models";
import { ApplicationViewCtx } from "~/views/customer/applications/ApplicationViewCtx";

function ABConfigurations() {
  const { application, updateApplication, canEdit } = useContext(
    ApplicationViewCtx
  );
  const configurations = application.abConfigurations;

  const addConfig = React.useCallback(
    (newConfig: ABConfiguration) => {
      return updateApplication({
        abConfigurations: [...configurations, newConfig],
      });
    },
    [configurations, updateApplication]
  );

  const removeConfig = React.useCallback(
    (idx) => {
      return updateApplication({
        abConfigurations: configurations.filter((_, i) => i !== idx),
      });
    },
    [configurations, updateApplication]
  );

  const formatLink = ({ value }: Cell<ABConfiguration>) => (
    <a href={value} target="_blank" rel="noopener noreferrer">
      {value}
    </a>
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Customer name",
        accessor: "customerName",
      },
      {
        Header: "Setup",
        accessor: "setup",
        Cell: formatLink,
      },
      {
        Header: "Policy",
        accessor: "policy",
        Cell: formatLink,
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }: Cell<ABConfiguration>) => {
          return (
            canEdit && (
              <ABConfigurationRemove
                removeCallback={removeConfig}
                configuration={row?.original}
                idx={row.index}
              />
            )
          );
        },
      },
    ],
    [canEdit, removeConfig]
  );

  return (
    <Box spacing="spacing04">
      <Row>
        <Col lg={12}>
          <FlexContainer>
            <Text weight="medium">AutoBackup configurations</Text>
            <ABConfigurationAdd addConfig={addConfig} />
          </FlexContainer>
          <DetailRow title="Backup Recovery Testing" minWidth="13rem">
            <InlineEditText
              name="backupRecoveryTesting"
              defaultValue={application.backupRecoveryTesting}
              onSubmit={(val) =>
                updateApplication({ backupRecoveryTesting: val })
              }
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Native Backups" minWidth="13rem">
            <InlineEditText
              name="nativeBackups"
              defaultValue={application.nativeBackups}
              onSubmit={(val) => updateApplication({ nativeBackups: val })}
              canEdit={canEdit}
            />
          </DetailRow>
          <DetailRow title="Restore" minWidth="13rem">
            <InlineEditText
              name="restore"
              defaultValue={application.restore}
              onSubmit={(val) => updateApplication({ restore: val })}
              canEdit={canEdit}
            />
          </DetailRow>
          <Table columns={columns} data={configurations} mb="0" />
        </Col>
      </Row>
    </Box>
  );
}

export { ABConfigurations };
